import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, ArrowUpRight, Loader2, Check } from "lucide-react";
import { api, errText } from "@/lib/api";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { trackFormStart, trackLead } from "@/lib/analytics";

const PILLARS = ["Strength", "Mobility", "Recovery", "Nutrition", "Mindset", "Community"];
const QUESTIONS = [
  { pillar: "Strength",  text: "I do resistance / weight training at least twice a week." },
  { pillar: "Strength",  text: "I feel confident lifting weights or using strength equipment." },
  { pillar: "Strength",  text: "I can see myself getting stronger — my numbers or capacity go up over time." },
  { pillar: "Mobility",  text: "I can move through my full range of motion without stiffness or pain." },
  { pillar: "Mobility",  text: "I actively work on mobility (stretching, mobility drills, yoga)." },
  { pillar: "Mobility",  text: "My joints feel loose and easy first thing in the morning." },
  { pillar: "Recovery",  text: "I get 7+ hours of quality sleep most nights." },
  { pillar: "Recovery",  text: "I take rest days without guilt." },
  { pillar: "Recovery",  text: "I have a wind-down routine that helps me switch off." },
  { pillar: "Nutrition", text: "My meals are built around whole foods — protein, vegetables, quality carbs." },
  { pillar: "Nutrition", text: "I eat enough protein for my body weight and activity level." },
  { pillar: "Nutrition", text: "I drink enough water throughout the day." },
  { pillar: "Mindset",   text: "I train even when I don't feel motivated." },
  { pillar: "Mindset",   text: "I have someone — coach, friend, community — who holds me accountable." },
  { pillar: "Mindset",   text: "I feel positive about my body and what it can do." },
  { pillar: "Community", text: "I train alongside other people rather than alone." },
  { pillar: "Community", text: "I have friends I've made through fitness or movement." },
  { pillar: "Community", text: "Being around others energises rather than drains me." },
];
const SCALE = [
  { v: 1, label: "Never" },
  { v: 2, label: "Rarely" },
  { v: 3, label: "Sometimes" },
  { v: 4, label: "Often" },
  { v: 5, label: "Always" },
];

export default function Scorecard() {
  const [stage, setStage] = useState("intro"); // intro | quiz | email | results
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState(Array(18).fill(null));
  const [contact, setContact] = useState({ name: "", email: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const answered = answers.filter((a) => a !== null).length;
  const progress = Math.round((answered / 18) * 100);

  const pick = (v) => {
    const next = [...answers];
    next[idx] = v;
    setAnswers(next);
    setTimeout(() => {
      if (idx < 17) setIdx(idx + 1);
      else setStage("email");
    }, 180);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/scorecard/submit", { ...contact, answers });
      setResult(data);
      setStage("results");
      trackLead({ form_name: "scorecard", enquiry_type: "scorecard" });
      toast.success("Report generated — also sent to your inbox.");
    } catch (err) {
      toast.error(errText(err) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="scorecard-page">
      <Seo
        title="Kore Method Scorecard — free 3-minute wellbeing assessment"
        description="Take the free 3-minute Kore Method Scorecard. Rate yourself across strength, mobility, recovery, nutrition, mindset and community — get a personalised report emailed to you."
        path="/scorecard"
      />

      {stage === "intro" && (
        <section className="bg-kore-forest text-kore-warm min-h-[92vh] flex items-center py-20">
          <div className="container-kore grid md:grid-cols-12 gap-14 items-center">
            <div className="md:col-span-7">
              <span className="text-[11px] uppercase tracking-[0.28em] text-kore-light">Free · 3 minutes</span>
              <h1 className="font-display font-light text-5xl md:text-7xl leading-[1.02] mt-5">
                The Kore Method<br/><span className="italic text-kore-light">Scorecard.</span>
              </h1>
              <p className="mt-8 text-kore-warm/80 leading-relaxed max-w-xl text-base md:text-lg">
                Rate yourself across the 6 areas we coach every member on. In 3 minutes you'll know exactly where you're strong, where to focus next, and what to change first. Your personalised report lands in your inbox.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <button type="button" onClick={() => { setStage("quiz"); trackFormStart({ form_name: "scorecard" }); }} data-testid="scorecard-start" className="btn-gold">
                  Start the scorecard <ArrowRight size={16} />
                </button>
                <Link to="/30-day-trial" className="btn-ghost !text-kore-warm !border-kore-warm/30">Or just try Kore Coaching</Link>
              </div>
              <p className="mt-6 text-xs text-kore-warm/50">18 quick questions · No sign-up · Free report</p>
            </div>
            <div className="md:col-span-4 md:col-start-9">
              <div className="rounded-3xl border border-kore-warm/15 p-8 bg-kore-forest/40 backdrop-blur">
                <div className="text-[11px] uppercase tracking-[0.24em] text-kore-light">The 6 areas</div>
                <ul className="mt-6 space-y-3">
                  {PILLARS.map((p, i) => (
                    <li key={p} className="flex items-baseline gap-3 text-kore-warm/90 text-sm">
                      <span className="text-kore-gold font-medium w-6">{String(i + 1).padStart(2, "0")}</span>
                      <span className="font-display text-xl text-kore-warm">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {stage === "quiz" && (
        <section className="min-h-[92vh] py-24 md:py-32 bg-kore-warm">
          <div className="container-kore max-w-2xl">
            <div className="flex items-center justify-between mb-8">
              <span className="text-[11px] uppercase tracking-[0.24em] text-kore-gold">{QUESTIONS[idx].pillar}</span>
              <span className="text-xs text-kore-ink/50">Question {idx + 1} of 18</span>
            </div>
            <div className="w-full bg-kore-ink/10 rounded-full h-1 mb-12">
              <div className="bg-kore-gold h-1 rounded-full transition-all" style={{ width: `${progress}%` }} data-testid="scorecard-progress" />
            </div>
            <motion.div key={idx} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
              <h2 className="display-2 text-kore-forest font-light leading-tight" data-testid="scorecard-question">
                {QUESTIONS[idx].text}
              </h2>
              <div className="mt-12 space-y-3" data-testid="scorecard-options">
                {SCALE.map((s) => {
                  const active = answers[idx] === s.v;
                  return (
                    <button
                      key={s.v}
                      type="button"
                      onClick={() => pick(s.v)}
                      data-testid={`scorecard-answer-${s.v}`}
                      className={`w-full text-left rounded-2xl border p-5 flex items-center justify-between transition ${active ? "border-kore-forest bg-kore-forest text-kore-warm" : "border-kore-ink/15 bg-white hover:border-kore-gold hover:bg-kore-warm/60"}`}
                    >
                      <span className={active ? "font-display text-2xl text-kore-warm" : "font-display text-2xl text-kore-forest"}>{s.label}</span>
                      <span className={active ? "text-kore-light text-sm" : "text-kore-ink/40 text-sm"}>{s.v} / 5</span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
            <div className="mt-10 flex justify-between items-center">
              <button
                type="button"
                onClick={() => setIdx(Math.max(0, idx - 1))}
                disabled={idx === 0}
                data-testid="scorecard-back"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-kore-ink/60 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ArrowLeft size={14} /> Back
              </button>
              <button
                type="button"
                onClick={() => { if (answers[idx] !== null) { if (idx < 17) setIdx(idx + 1); else setStage("email"); } }}
                disabled={answers[idx] === null}
                data-testid="scorecard-next"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-kore-forest disabled:opacity-30 disabled:cursor-not-allowed border-b border-kore-gold pb-1"
              >
                {idx === 17 ? "Get my report" : "Next"} <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </section>
      )}

      {stage === "email" && (
        <section className="min-h-[92vh] py-24 md:py-32 bg-kore-forest text-kore-warm flex items-center">
          <div className="container-kore max-w-2xl">
            <span className="text-[11px] uppercase tracking-[0.28em] text-kore-light">Nearly there</span>
            <h2 className="font-display font-light text-4xl md:text-5xl leading-tight mt-5">Where should we send your report?</h2>
            <p className="mt-6 text-kore-warm/80 leading-relaxed max-w-xl">Your personalised Kore Method report is ready. Pop your name and email below and we'll send it straight through — plus you'll see it on screen right after.</p>
            <form onSubmit={submit} onFocus={() => trackFormStart({ form_name: "scorecard_contact" })} data-testid="scorecard-email-form" className="mt-10 space-y-6 max-w-xl">
              <div>
                <label className="eyebrow !text-kore-light">Name</label>
                <input required data-testid="scorecard-name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} className="mt-2 w-full border-b border-kore-warm/30 py-2 bg-transparent text-kore-warm focus:outline-none focus:border-kore-light" placeholder="Your first name" />
              </div>
              <div>
                <label className="eyebrow !text-kore-light">Email</label>
                <input required type="email" data-testid="scorecard-email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="mt-2 w-full border-b border-kore-warm/30 py-2 bg-transparent text-kore-warm focus:outline-none focus:border-kore-light" placeholder="you@example.com" />
              </div>
              <label className="flex items-start gap-3 text-[13px] text-kore-warm/75 leading-relaxed">
                <input type="checkbox" data-testid="scorecard-consent" checked={contact.consent} onChange={(e) => setContact({ ...contact, consent: e.target.checked })} className="mt-1 h-4 w-4" />
                <span>Yes, send me tips &amp; offers from KORE (optional). Unsubscribe anytime.</span>
              </label>
              <button type="submit" disabled={submitting} data-testid="scorecard-submit" className="btn-gold disabled:opacity-70">
                {submitting ? <><Loader2 size={14} className="animate-spin" /> Building your report…</> : <>See my report <ArrowRight size={14} /></>}
              </button>
              <p className="text-xs text-kore-warm/50">We'll email your report and one short follow-up from Dale. Your details stay with Kore — see our <Link to="/privacy" className="underline">privacy notice</Link>.</p>
            </form>
          </div>
        </section>
      )}

      {stage === "results" && result && (
        <section className="py-24 md:py-32 bg-kore-warm">
          <div className="container-kore max-w-4xl">
            <span className="text-[11px] uppercase tracking-[0.24em] text-kore-gold">Your Kore Method Scorecard</span>
            <h2 className="display-1 text-kore-forest font-light leading-[1.05] mt-5" data-testid="scorecard-results-heading">Here's where you are, {contact.name.split(" ")[0]}.</h2>

            {/* Wheel + overall score */}
            <div className="mt-14 rounded-3xl bg-kore-forest text-kore-warm p-10 md:p-14 grid md:grid-cols-2 gap-10 items-center">
              <div className="flex justify-center">
                <svg viewBox="0 0 220 220" width="240" height="240" data-testid="scorecard-donut">
                  <circle cx="110" cy="110" r="90" fill="none" stroke="#F7F7F3" strokeOpacity="0.18" strokeWidth="18"/>
                  <circle
                    cx="110" cy="110" r="90"
                    fill="none" stroke="#CAFBCE" strokeWidth="18"
                    strokeDasharray={`${(2 * Math.PI * 90 * result.overall) / 100} ${2 * Math.PI * 90}`}
                    strokeDashoffset={(2 * Math.PI * 90) / 4}
                    strokeLinecap="round"
                    transform="rotate(-90 110 110)"
                    style={{ transition: "stroke-dasharray 900ms ease-out" }}
                  />
                  <text x="110" y="106" textAnchor="middle" fill="#F7F7F3" fontFamily="Georgia, serif" fontSize="64" fontWeight="300">{result.overall}</text>
                  <text x="110" y="134" textAnchor="middle" fill="#CAFBCE" fontFamily="Arial, sans-serif" fontSize="12" letterSpacing="2">OUT OF 100</text>
                </svg>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.24em] text-kore-light">Overall wellbeing score</div>
                <h3 className="font-display font-light text-4xl md:text-5xl mt-3 leading-tight" data-testid="scorecard-overall">
                  {result.overall < 40 ? "Room to build" : result.overall < 55 ? "Foundations" : result.overall < 70 ? "Building well" : result.overall < 85 ? "Strong" : "Excellent"}
                </h3>
                <p className="mt-5 text-kore-warm/75 leading-relaxed text-sm md:text-base">
                  This is your personalised wellbeing snapshot across the 6 areas we coach every KORE member on. Below, we've broken down each one, why it matters, and the specific five steps to move your score in the next 30 days.
                </p>
              </div>
            </div>

            {/* Priority focus areas */}
            <div className="mt-16">
              <span className="eyebrow">Focus next on</span>
              <h3 className="display-2 mt-4 text-kore-forest font-light">Your two biggest levers this month.</h3>
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                {result.focus.map((f, i) => (
                  <div key={f} className="rounded-3xl bg-kore-forest text-kore-warm p-8">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-kore-light">Priority #{i + 1}</div>
                    <div className="font-display text-4xl mt-3">{f}</div>
                    <div className="mt-2 text-kore-warm/70 text-sm">{result.pillars[f].score}% · {result.pillars[f].headline}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Deep-dive per pillar */}
            <div className="mt-16">
              <span className="eyebrow">Deep-dive</span>
              <h3 className="display-2 mt-4 text-kore-forest font-light">Your report, broken down.</h3>
              <div className="mt-10 space-y-8" data-testid="scorecard-pillars">
                {PILLARS.map((p) => {
                  const pd = result.pillars[p];
                  const bandColor = pd.band === "low" ? "bg-kore-forest text-kore-warm" : pd.band === "med" ? "bg-kore-gold text-kore-forest" : "bg-kore-light text-kore-forest";
                  const barColor = pd.band === "low" ? "bg-kore-forest" : pd.band === "med" ? "bg-kore-gold" : "bg-kore-light";
                  const bandLabel = pd.band === "low" ? "Focus area" : pd.band === "med" ? "Building" : "Strong";
                  return (
                    <div key={p} className="rounded-3xl bg-white border border-kore-ink/10 p-8 md:p-10 shadow-soft">
                      <div className="flex justify-between items-baseline flex-wrap gap-3">
                        <span className="font-display text-3xl text-kore-forest">{p}</span>
                        <span className={`${bandColor} text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full`}>
                          {pd.band === "low" ? "Growth area" : pd.band === "med" ? "Building" : "Strong"} · {pd.score}%
                        </span>
                      </div>
                      <div className="mt-4 bg-kore-ink/10 rounded-full h-2 overflow-hidden">
                        <div className={`${barColor} h-2`} style={{ width: `${pd.score}%` }} />
                      </div>
                      <h4 className="font-display text-2xl md:text-3xl text-kore-forest mt-6 leading-tight">{pd.headline}</h4>
                      <p className="mt-3 text-kore-ink/75 leading-relaxed">{pd.summary}</p>

                      <div className="mt-8">
                        <div className="text-[11px] uppercase tracking-[0.22em] text-kore-gold">Five things worth trying</div>
                        <ol className="mt-4 space-y-3">
                          {pd.actions.map((a, i) => (
                            <li key={i} className="flex gap-3 text-kore-ink/85 leading-relaxed">
                              <span className="text-kore-gold font-medium shrink-0">{i + 1}.</span>
                              <span>{a}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Soft closing note */}
            <div className="mt-20 rounded-3xl bg-kore-forest text-kore-warm p-10 md:p-14 text-center">
              <span className="text-[11px] uppercase tracking-[0.24em] text-kore-light">A gentle note</span>
              <h3 className="font-display font-light text-3xl md:text-4xl mt-4 leading-tight">This report is yours — do what feels right with it.</h3>
              <p className="mt-5 text-kore-warm/75 max-w-xl mx-auto leading-relaxed">
                Save it, share it, work through the actions in your own time. If you'd ever like coached support with any of the pillars above, we'd be glad to chat — no pressure, no timetable.
              </p>
              <div className="mt-8 flex flex-wrap gap-4 justify-center">
                <Link to="/contact" data-testid="results-cta-contact" className="btn-ghost !text-kore-warm !border-kore-warm/40">Book a friendly consultation</Link>
                <Link to="/30-day-trial" data-testid="results-cta-trial" className="text-kore-light underline underline-offset-4 text-sm py-3 px-4">Explore the 30-day KORE Coaching Trial</Link>
              </div>
              <p className="mt-8 text-xs text-kore-warm/50">Your full report has been emailed to {contact.email}.</p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
